import {
  CATEGORIES,
  DOWNLOAD_STATUSES,
  type DownloadJobType,
  type DownloadStatus,
  type GalleryCardType,
} from "@volgo/core/contract";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { isActive } from "../downloads/poll";
import { downloadProgress } from "../downloads/progress";
import { useDownloadWatch } from "../downloads/useDownloadWatch";
import { type GalleryInfo, useGalleryInfo } from "../downloads/useGalleryInfo";
import { useServerBlocks } from "../policy/blocked";
import { fromSearchParams, toggleCategory, toSearchParams } from "../search/query";
import { useSession } from "../session";
import { Button } from "../ui/Button";
import { CategoryFilter } from "../ui/CategoryFilter";
import { describeDownloadFailure } from "../ui/errors";
import { formatDateTime } from "../ui/format";
import { GalleryCard } from "../ui/GalleryCard";
import { GalleryLink } from "../ui/GalleryLink";
import { useGallerySource } from "../ui/GallerySource";
import { DOWNLOAD_STATUS_LABELS, ROUTE_LABELS } from "../ui/labels";
import { PagedFooter } from "../ui/PagedFooter";
import { Segmented } from "../ui/Segmented";
import { ErrorNote } from "../ui/States";
import { useLoad, usePaged } from "../ui/useLoad";

type Filter = "all" | DownloadStatus;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "전체" },
  ...DOWNLOAD_STATUSES.map((value) => ({ value, label: DOWNLOAD_STATUS_LABELS[value] })),
];

export function JobProgress({ job }: { job: DownloadJobType }) {
  if (job.status === "failed")
    return <span className="note-warn">{describeDownloadFailure(job.errorCode)}</span>;
  if (job.status === "done") return <span>{job.pageCount ?? job.progress.done}쪽</span>;
  const { done, total, label, text } = downloadProgress(job);
  return (
    <span>
      {text}
      {total ? <progress value={done} max={total} aria-label={label} /> : null}
    </span>
  );
}

/** The notice a blocked gallery's row carries instead of a read link. */
export function BlockedMark() {
  return <span className="note row-blocked">차단된 태그가 포함되어 있습니다</span>;
}

function DownloadOverlay({ job }: { job: DownloadJobType }) {
  if (job.status === "done") return null;
  const active = isActive(job);
  const { total, done, label, count, text } = downloadProgress(job);
  const percent = total ? Math.round((done / total) * 100) : null;
  return (
    <div className="download-overlay">
      {active ? (
        <div
          className="download-ring"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={total || undefined}
          aria-valuenow={total ? done : undefined}
          aria-valuetext={text}
        >
          <svg viewBox="0 0 48 48" aria-hidden="true">
            <circle className="ring-track" cx="24" cy="24" r="20" />
            <circle
              className={total ? "ring-value" : "ring-value ring-pending"}
              cx="24"
              cy="24"
              r="20"
              pathLength="100"
              strokeDasharray={`${percent ?? 24} 100`}
            />
          </svg>
          <span aria-hidden="true">{percent === null ? "…" : `${percent}%`}</span>
        </div>
      ) : (
        <span className="download-symbol" aria-hidden="true">
          ×
        </span>
      )}
      <b>{label}</b>
      {active && count && <span>{count}</span>}
    </div>
  );
}

function JobCard({ job, info, card }: { job: DownloadJobType; info: GalleryInfo; card?: GalleryCardType }) {
  const blocked = info.verdict === "blocked" || card?.policy.level === "block";
  const details = (
    <>
      <p className="card-extra">
        {ROUTE_LABELS[job.route]} · {formatDateTime(job.createdAt)}
      </p>
      <p className="download-caption">
        {DOWNLOAD_STATUS_LABELS[job.status]}
        {job.status === "done" ? ` · ${job.pageCount ?? job.progress.done}쪽` : ""}
      </p>
      {job.status === "failed" && <p className="note note-warn">{describeDownloadFailure(job.errorCode)}</p>}
      {blocked && <BlockedMark />}
    </>
  );
  return (
    <li
      className="download-card"
      data-status={job.status}
      data-gid={job.gid}
      data-policy={blocked ? "blocked" : info.verdict}
    >
      {card && info.verdict !== "unknown" && (!blocked || card.policy.level === "block") ? (
        <GalleryCard card={card} to={`/g/${job.gid}`} coverOverlay={<DownloadOverlay job={job} />}>
          {details}
        </GalleryCard>
      ) : (
        <article className="card">
          <div className="cv">
            <DownloadOverlay job={job} />
          </div>
          <h3>
            <GalleryLink gid={job.gid}>{info.title ?? `갤러리 ${job.gid}`}</GalleryLink>
          </h3>
          {info.title === null && <p className="note">제목 미수집</p>}
          {details}
        </article>
      )}
    </li>
  );
}

/** All my jobs use cards; server filtering happens before pagination. */
export function DownloadsPage() {
  const { client } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const rawStatus = params.get("status");
  const filter: Filter = DOWNLOAD_STATUSES.find((s) => s === rawStatus) ?? "all";
  const query = useMemo(() => fromSearchParams(new URLSearchParams(location.search)), [location.search]);
  const setFilter = (status: Filter) => {
    const next = new URLSearchParams(location.search);
    if (status === "all") next.delete("status");
    else next.set("status", status);
    navigate({ pathname: "/downloads", search: next.toString() }, { replace: true });
  };
  const blocks = useServerBlocks();
  const noteBlocked = blocks.note;
  const list = usePaged<DownloadJobType>(
    (cursor) =>
      query.categories.length === 0
        ? Promise.resolve({ items: [], nextCursor: null })
        : client.call("downloads.list", {
            query: {
              limit: 80,
              ...(filter === "all" ? {} : { status: filter }),
              ...(query.categories.length < CATEGORIES.length ? { category: [...query.categories] } : {}),
              ...(cursor ? { cursor } : {}),
            },
          }),
    `${filter}:${query.categories.join(",")}`,
  );
  const { setItems } = list;
  const merge = useCallback(
    (job: DownloadJobType) => {
      setItems((items) => items.map((j) => (j.id === job.id ? job : j)));
      if (job.status === "failed" && job.errorCode === "POLICY_BLOCKED") noteBlocked(job.gid);
    },
    [setItems, noteBlocked],
  );
  const watch = useDownloadWatch(client, list.items, merge);
  const infoOf = useGalleryInfo(list.items);
  const gids = useMemo(() => [...new Set(list.items.map((j) => j.gid))], [list.items]);
  const cards = useLoad(async () => {
    const items: GalleryCardType[] = [];
    for (let i = 0; i < gids.length; i += 100) {
      const page = await client.call("catalog.cards", { body: { gids: gids.slice(i, i + 100) } });
      items.push(...page.items);
    }
    const byGid = new Map(items.map((card) => [card.gid, card]));
    return gids.flatMap((gid) => byGid.get(gid) ?? []);
  }, gids.join(","));
  const source = useMemo(() => cards.data ?? [], [cards.data]);
  useGallerySource(source);
  const byGid = new Map(source.map((c) => [c.gid, c]));

  return (
    <div className="page downloads-page">
      <CategoryFilter
        selected={query.categories}
        onToggle={(category) => {
          const next = toSearchParams(toggleCategory(query, category));
          if (filter !== "all") next.set("status", filter);
          navigate({ pathname: "/downloads", search: next.toString() }, { replace: true });
        }}
      />
      <div className="page-head">
        <h1 tabIndex={-1}>다운로드</h1>
        <Segmented legend="상태" options={FILTERS} isOn={(v) => v === filter} onPick={setFilter} />
      </div>
      {watch.state === "timeout" && (
        <p className="note note-warn" role="status">
          오래 걸리는 작업이 있어 진행 확인을 멈췄습니다. <Button onClick={watch.resume}>다시 확인</Button>
        </p>
      )}
      {watch.state === "error" && (
        <p className="note note-warn" role="alert">
          진행 상황을 확인하지 못했습니다. <Button onClick={watch.resume}>다시 확인</Button>
        </p>
      )}
      {cards.status === "error" && <ErrorNote error={cards.error} onRetry={cards.reload} />}
      {list.items.length > 0 && (
        <ul className="grid download-grid" aria-label="다운로드 목록">
          {list.items.map((job) => (
            <JobCard key={job.id} job={job} info={infoOf(job)} card={byGid.get(job.gid)} />
          ))}
        </ul>
      )}
      <PagedFooter list={list} emptyTitle="다운로드가 없습니다" moreLabel="↓ 80개 더 보기">
        <p>작품 창에서 “내려받기”를 누르면 여기에 진행 상황이 나옵니다. 선택한 분류와 상태도 확인하세요.</p>
      </PagedFooter>
    </div>
  );
}
